import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFacilities } from '../hooks/useFacilities';
import { toast } from 'sonner';
import BookingDrawer from '../components/BookingDrawer';

/* ── Avatar helpers ──────────────────────────────────────────────────────────*/
const AV_COLORS = ['c0', 'c1', 'c2', 'c3', 'c4'];
function initials(name = '') {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}
function avc(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 5;
  return AV_COLORS[h];
}

function Stat({ label, value, variant, sub }) {
  return (
    <div className="sb-card">
      <div className="sb-stat">
        <div className="sb-stat-lbl">{label}</div>
        <div className="sb-stat-val" style={{ fontSize: 28, color: variant === 'warn' ? 'var(--warn-fg)' : variant === 'ok' ? 'var(--ok-fg)' : 'var(--ink)' }}>
          {typeof value === 'number' && label.toLowerCase().includes('amount') ? (
            <><span className="sb-stat-ccy" style={{ fontSize: 16 }}>₹</span>{value.toLocaleString('en-IN')}</>
          ) : value}
        </div>
        {sub && <div className="sb-delta" style={{ color: variant === 'warn' ? 'var(--warn-fg)' : 'var(--ink-3)' }}>{sub}</div>}
      </div>
    </div>
  );
}

function RefundRow({ b, facilityName, tab, api, onMutated, onViewDrawer }) {
  const [acting, setActing] = useState(false);
  const paid = parseFloat(b.payment?.amount) || 0;
  const ini = initials(b.customer_name);
  const colour = avc(b.customer_name);

  const markRefunded = async (e) => {
    e.stopPropagation();
    setActing(true);
    try {
      await api.patch(`/api/bookings/${b.booking_id}/refund-status`, { refund_processed: true });
      toast.success('Marked as refunded');
      onMutated();
    } catch { toast.error('Failed'); } finally { setActing(false); }
  };

  const unmark = async (e) => {
    e.stopPropagation();
    setActing(true);
    try {
      await api.patch(`/api/bookings/${b.booking_id}/refund-status`, { refund_processed: false });
      toast.success('Unmarked');
      onMutated();
    } catch { toast.error('Failed'); } finally { setActing(false); }
  };

  return (
    <div className="sb-refund-row" style={{ cursor: 'pointer' }} onClick={() => onViewDrawer(b)}>
      {/* Customer */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div className={`sb-av ${colour}`} style={{ width: 32, height: 32, fontSize: 11, flexShrink: 0 }}>{ini}</div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {b.customer_name}
          </div>
          <div className="sb-mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{b.customer_phone}</div>
        </div>
      </div>

      {/* Booking info */}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 500, fontSize: 13, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {b.ground_id} · {facilityName}
        </div>
        <div className="sb-mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          {b.booking_id?.slice(0, 8).toUpperCase()}
        </div>
      </div>

      {/* Cancelled context */}
      <div>
        <div className="sb-mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>{b.date}</div>
        <div className="sb-mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{b.slot_time}</div>
      </div>

      {/* Amount */}
      <div className="sb-mono" style={{
        fontWeight: 600,
        fontSize: 14,
        color: tab === 'processed' ? 'var(--ok-fg)' : tab === 'ineligible' ? 'var(--ink-3)' : 'var(--warn-fg)',
      }}>
        {paid > 0 ? `₹${paid}` : '—'}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={e => e.stopPropagation()}>
        <button className="sb-btn ghost sm" onClick={() => onViewDrawer(b)}>Details</button>
        {tab === 'pending' && (
          <button className="sb-btn brand sm" disabled={acting} onClick={markRefunded}>
            {acting ? '…' : 'Mark refunded'}
          </button>
        )}
        {tab === 'processed' && (
          <button className="sb-btn ghost sm" disabled={acting} onClick={unmark}>
            {acting ? '…' : 'Unmark'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminRefunds() {
  const { api } = useAuth();
  const { facilities } = useFacilities();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [drawerBooking, setDrawerBooking] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all cancelled bookings
      const res = await api.get('/api/bookings/', { params: { status: 'cancelled', page_size: 200 } });
      setBookings(res.data.bookings || []);
    } catch {
      toast.error('Failed to load refunds');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Build facility name lookup
  const facMap = useMemo(() => {
    const m = {};
    for (const f of facilities) m[f.location_id] = f.name;
    return m;
  }, [facilities]);

  // Categorise
  const pending    = bookings.filter(b => b.refund_eligible && !b.refund_processed);
  const processed  = bookings.filter(b => b.refund_eligible && b.refund_processed);
  const ineligible = bookings.filter(b => !b.refund_eligible);

  const pendingAmount   = pending.reduce((s, b) => s + (parseFloat(b.payment?.amount) || 0), 0);
  const processedAmount = processed.reduce((s, b) => s + (parseFloat(b.payment?.amount) || 0), 0);

  const rows = tab === 'pending' ? pending : tab === 'processed' ? processed : ineligible;

  const drawerFacility = drawerBooking ? facMap[drawerBooking.location_id] : null;

  const tabs = [
    { key: 'pending',    label: `Pending ${pending.length > 0 ? pending.length : ''}` },
    { key: 'processed',  label: `Processed ${processed.length > 0 ? processed.length : ''}` },
    { key: 'ineligible', label: `Ineligible ${ineligible.length > 0 ? ineligible.length : ''}` },
  ];

  return (
    <div>
      <div className="sb-page-head">
        <div>
          <div className="sb-crumb">Operations</div>
          <h1>Refunds</h1>
          <div className="sb-sub">Cancelled bookings with money owed back to customers.</div>
        </div>
      </div>

      {/* Stat strip */}
      <div className="sb-stat-strip" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <Stat
          label="Pending refund amount"
          value={pendingAmount}
          variant={pending.length > 0 ? 'warn' : 'ok'}
          sub={`${pending.length} booking${pending.length !== 1 ? 's' : ''} to process`}
        />
        <Stat
          label="Refunded all-time"
          value={processedAmount}
          variant="ok"
          sub={`${processed.length} completed`}
        />
        <Stat
          label="No-refund cancels"
          value={ineligible.length}
          sub="Within policy window or no payment"
        />
      </div>

      {/* Tab chipset */}
      <div style={{ marginBottom: 20 }}>
        <div className="sb-chipset">
          {tabs.map(t => (
            <div key={t.key} className={`sb-chip ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
              {t.label}
            </div>
          ))}
        </div>
      </div>

      {/* Table card */}
      <div className="sb-card" style={{ overflow: 'hidden' }}>
        <div className="x-scroll"><div style={{ minWidth: 680 }}>
        {/* Header */}
        <div className="sb-refund-row header">
          <span>Customer</span>
          <span>Booking</span>
          <span>Cancelled</span>
          <span>Refund amount</span>
          <span>Action</span>
        </div>

        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="sb-refund-row" style={{ opacity: .5 }}>
              {Array.from({ length: 5 }).map((_, j) => (
                <div key={j} style={{ height: 14, background: 'var(--line-soft)', borderRadius: 4 }} />
              ))}
            </div>
          ))
        ) : rows.length === 0 ? (
          <div style={{ padding: '60px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
              {tab === 'pending'
                ? 'No pending refunds — all clear ✓'
                : tab === 'processed'
                  ? 'No processed refunds yet.'
                  : 'No ineligible cancellations.'}
            </div>
          </div>
        ) : (
          rows.map(b => (
            <RefundRow
              key={b.booking_id}
              b={b}
              facilityName={facMap[b.location_id] || `Location ${b.location_id}`}
              tab={tab}
              api={api}
              onMutated={fetchAll}
              onViewDrawer={setDrawerBooking}
            />
          ))
        )}
        </div></div>
      </div>

      {/* Booking drawer */}
      {drawerBooking && (
        <BookingDrawer
          booking={drawerBooking}
          facilityName={drawerFacility}
          slotTotal={0}
          api={api}
          onClose={() => setDrawerBooking(null)}
          onMutated={() => { fetchAll(); setDrawerBooking(null); }}
        />
      )}
    </div>
  );
}
